// PresentAsPowerWithGivenBaseTask.js
// Zadanie: Dana liczba w formie ułamka 1/N. Przedstaw ją jako potęgę o PODANEJ podstawie a.
// Zakresy wykładnika jak w zadaniu 1: a=2 → k≤6; a=3 → k≤4; a∈{4..9} → k≤3.
// W poleceniu pokazujemy N = a^k (policzone), nie 1/a^k. Wykluczamy k=1.

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput from "../../../../components/inputs/FractionInput";

const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];
const range = (a, b) => Array.from({ length: b - a + 1 }, (_, i) => a + i);

function parseUser(raw) {
  if (!raw) return null;
  const s = raw.replace(/\s/g, "");

  // a^-k
  let m = s.match(/^(\d+)\^\-(\d+)$/);
  if (m) return { kind: "negPow", base: parseInt(m[1], 10), k: parseInt(m[2], 10) };

  // 1/a^k
  m = s.match(/^1\/(\d+)\^(\d+)$/);
  if (m) return { kind: "fracPow", base: parseInt(m[1], 10), k: parseInt(m[2], 10) };

  // 1/(a^k)
  m = s.match(/^1\/\((\d+)\^(\d+)\)$/);
  if (m) return { kind: "fracPowParen", base: parseInt(m[1], 10), k: parseInt(m[2], 10) };

  return null;
}

// ładne formatowanie odpowiedzi użytkownika (różne warianty zapisu)
function prettyUserLatex(raw) {
  if (!raw) return "";
  const s = String(raw).replace(/\s/g, "");
  let m;

  // 1/(a^k)
  m = s.match(/^1\/\((\d+)\^(\d+)\)$/);
  if (m) return `\\dfrac{1}{${m[1]}^{${m[2]}}}`;

  // 1/a^k
  m = s.match(/^1\/(\d+)\^(\d+)$/);
  if (m) return `\\dfrac{1}{${m[1]}^{${m[2]}}}`;

  // a^-k
  m = s.match(/^(\d+)\^\-(\d+)$/);
  if (m) return `${m[1]}^{-${m[2]}}`;

  // fallback – pokaż tak jak wpisano
  return s;
}

function powerInt(a, k) {
  let res = 1;
  for (let i = 0; i < k; i++) res *= a;
  return res;
}

export function generateTask() {
  const requiredBase = [2, 3, 4, 5, 6, 7, 8, 9][Math.floor(Math.random() * 8)];

  let maxK = 3;
  if (requiredBase === 2) maxK = 6;
  else if (requiredBase === 3) maxK = 4;

  // k ∈ [2..maxK] — wykluczamy k=1 (żeby nie wychodził egz. a^{-1})
  const ks = range(2, maxK);
  const k = pick(ks);

  const denom = powerInt(requiredBase, k); // N = a^k
  const valueLatex = `\\dfrac{1}{${denom}}`;
  const answerExp = -k; // wynik to a^{-k}

  return {
    type: "PresentAsPowerWithGivenBaseTask",
    data: { requiredBase, k, denom, valueLatex, answerExp },
  };
}

export function renderTask({ data }) {
  return (
    <div style={{ marginBottom: "12px" }}>
      Przedstaw liczbę <InlineMath math={'{'+data.valueLatex+'}'} /> w postaci potęgi o podstawie{" "}
      <InlineMath math={'{'+data.requiredBase+'}'} />.
    </div>
  );
}


export function renderInput(task, userAnswer, onChange, disabled) {
  const { requiredBase, k } = task.data;
  const handleChange = (ans) => onChange?.(ans);

  return (
    <div>
      <FractionInput
        value={userAnswer}
        onChange={handleChange}
        disabled={disabled}
        placeholder={"np. 5^-2 lub 5^2"}
        label=""
        powerPreview={true}
      />
      {/* te dwa wstawki są jak w innych zadaniach → identyczny odstęp */}
      <div aria-hidden style={{ marginTop: "-16px" }} />
      <div aria-hidden style={{ height: "12px" }} />
    </div>
  );
}

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.raw) return false;
  const parsed = parseUser(userAnswer.raw);
  if (!parsed) return false;

  const { requiredBase, k } = data;

  switch (parsed.kind) {
    case "negPow":
      return parsed.base === requiredBase && parsed.k === k;
    case "fracPow":
    case "fracPowParen":
      return parsed.base === requiredBase && parsed.k === k;
    default:
      return false;
  }
}

export function renderUserAnswer(userAnswer, task) {
  // UWAGA: UniversalQuizRunner wywołuje (userAnswer, task).
  // Tutaj nie potrzebujemy taska – formatujemy tylko wpis użytkownika.
  if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.raw)
    return <i>(brak odpowiedzi)</i>;

  // ładne formatowanie:  a^-k  /  1/a^k  /  1/(a^k)
  const s = String(userAnswer.raw).replace(/\s/g, "");
  let m;

  // 1/(a^k)
  m = s.match(/^1\/\((\d+)\^(\d+)\)$/);
  if (m) return <InlineMath math={`\\dfrac{1}{${m[1]}^{${m[2]}}}`} />;

  // 1/a^k
  m = s.match(/^1\/(\d+)\^(\d+)$/);
  if (m) return <InlineMath math={`\\dfrac{1}{${m[1]}^{${m[2]}}}`} />;

  // a^-k
  m = s.match(/^(\d+)\^\-(\d+)$/);
  if (m) return <InlineMath math={`${m[1]}^{-${m[2]}}`} />;

  // fallback
  return <InlineMath math={s} />;
}


export function renderAnswer({ data }) {
  const { requiredBase, answerExp } = data;
  return <InlineMath math={'{'+`${requiredBase}^{${answerExp}}`+'}'} />;
}

function expandMultiplicationLatex(a, k) {
  // pokazujemy iloczyn tylko dla k ≤ 6, żeby było czytelnie
  if (k > 6) return null;
  return Array.from({ length: k }, () => `${a}`).join('\\cdot ');
}

export function renderExplanation({ data }) {
  const { requiredBase: a, k, denom, valueLatex, answerExp } = data;

  const mult = expandMultiplicationLatex(a, k);
  const powLine = mult ? `${a}^{${k}}\\;=\\;${mult}\\;=\\;${denom}` : `${a}^{${k}}\\;=\\;${denom}`;

  return (
    <div style={{ display: "grid", rowGap: "8px" }}>
      <p><strong>Krok 1:</strong> Oblicz mianownik jako potęgę podstawy.</p>
      <BlockMath math={'{'+powLine+'}'} />

      <p><strong>Krok 2:</strong> Skorzystaj z <InlineMath math={'{\\;\\dfrac{1}{a^{k}}=a^{-k}\\;}'}/> i zapisz liczbę jako potęgę o podstawie <InlineMath math={'{'+a+'}'} />.</p>
      <BlockMath math={'{'+`${valueLatex}\\;=\\;\\dfrac{1}{${a}^{${k}}}\\;=\\;${a}^{-${k}}`+'}'} />

      <p><strong>Wynik:</strong></p>
      <BlockMath math={'{'+`${a}^{${answerExp}}`+'}'} />
    </div>
  );
}
